import logging from '~/stores/providers/logging';
import Future, { FutureInstance, lastly } from 'fluture';
import { isBoolean, isEmpty } from 'lodash';
import {
  addIndex,
  allPass,
  always,
  complement,
  concat,
  converge,
  curry,
  equals,
  F,
  flip,
  identity,
  ifElse,
  indexOf,
  isNil,
  last,
  map,
  mergeDeepRight,
  objOf,
  path,
  pipe,
  pluck,
  propEq,
  propOr,
  reject,
  split,
  startsWith,
  T,
  unapply,
  useWith as ramdaUseWith,
  zipObj,
} from 'ramda';
import is from 'ramda/es/is';
import keys from 'ramda/es/keys';
import mergeAll from 'ramda/es/mergeAll';
import reverse from 'ramda/es/reverse';
import sortBy from 'ramda/es/sortBy';
import { Maybe } from 'ramda-fantasy';

import _isNonEmpty from './isNonEmpty';
import _rename from './rename';

export { _rename as rename };
export { _isNonEmpty as isNonEmpty };

export const isArray = is(Array);

export const isFunction = is(Function);

export const isNumber = is(Number);

export const isString = is(String);

export const propOrEmptyStr = (prop: string) => propOr('', prop);

export const includesInsensitiveNonCurry = (valueString: string, allString: string): boolean => {
  return indexOf(valueString.toLowerCase(), allString.toLowerCase()) > -1;
};

export const includesInsensitive = curry(includesInsensitiveNonCurry);

export const startsWithInsensitive = (valueString: string, allString: string) => {
  return startsWith(valueString.toLowerCase(), allString.toLowerCase());
};

export const propOfPropValue = curry((prop: string, obj: object) => obj[obj[prop]]);

export const logTap = curry((tag, x) => {
  logging.log(tag, x);
  return x;
});

export const logTapBy = curry((tag, by, x) => {
  logging.log(tag, by(x));
  return x;
});

export const logTimeTap = curry((label, fn) => {
  return (...args) => {
    const start = Date.now();
    const result = fn(...args);
    logging.log(label, Date.now() - start);
    return result;
  };
});

export const logReturn = curry((label, fn) => {
  return (...args) => {
    const result = fn(...args);
    logging.log(label, result);
    return result;
  };
});

export const mapIndexed = addIndex(map);

export const delayedFn = curry((fn, delay) => {
  return setTimeout(fn, delay);
});

export const shiftLeft = curry((amount, val) => {
  // tslint:disable-next-line: no-bitwise
  return val << amount;
});

export const shiftRight = curry((amount, val) => {
  // tslint:disable-next-line: no-bitwise
  return val >> amount;
});

export const isPathEmpty = curry((pathArray, obj) => {
  return pipe(path(pathArray), isEmpty)(obj);
});

export const list = unapply(identity);

export const rejectEmpty = reject(allPass([isEmpty, complement(isNumber), complement(isBoolean)]));

export const wrapFuture =
  (fn) =>
  (...args) => {
    return Future((rej, res) => {
      fn(...args)
        .catch(rej)
        .then(res);
    });
  };

export const isNonNil = complement(isNil);

export const generateRandomNumber = (min, max = min) => {
  return Math.random() * (max - min) + min;
};

export const keyBy = curry(<T>(keyName: string, list: T[]) => {
  return converge<any, any, any, any>(zipObj, [pluck(keyName), identity])(list);
});

export const runFutureLastly = curry(
  <L extends FutureInstance<any, any>, O extends FutureInstance<any, any>>(finallyFuture: L, originFuture: O) => {
    return lastly(finallyFuture, originFuture);
  }
);

export const isLastIndex = curry(<T>(list: T[], index) => {
  return index === list.length - 1;
});

export const lastSplitPart = curry((ch: string, str: string) => {
  return pipe(split(ch), last)(str);
});

export const wrapValues = curry(
  <T, K extends keyof T, IK extends string>(ik: IK, o: T): Record<K, Record<IK, T[K]>> => {
    return map(objOf(ik), o);
  }
);

export const setSideKeyNameToValue = <L, R, LK extends keyof L, RK extends keyof R>(
  left: L,
  right: R
): Record<string, { leftValue?: L[LK]; rightValue?: R[RK] }> => {
  return mergeDeepRight(wrapValues('leftValue', left), wrapValues('rightValue', right)) as Record<
    string,
    { leftValue?: L[LK]; rightValue?: R[RK] }
  >;
};

export const diffObjs = <L extends object>(left: L, right: object): Partial<L> => {
  const keys = Object.keys(left);
  return keys.reduce((acc, key) => {
    if (left[key] !== right[key]) {
      acc[key] = left[key];
    }
    return acc;
  }, {});
};

export const applyWith = curry((withFn, fn, obj) => {
  return pipe<any, any, any>(withFn, fn)(obj);
});

export const isNonEquals: <T>(value: T, other: T) => boolean = complement(equals);

// @ts-ignore
export const defaults = (...args: any[]) => pipe<any, any, any, any>(unapply(identity), reverse, mergeAll)(...args);

export const sortedKeys = ramdaUseWith(sortBy(identity), [keys]);

export const boolToString = ifElse(equals(true), always('true'), always('false'));

export const stringToBool = ifElse(equals('true'), T, F);

export const eitherBy = curry(<T, U>(f: (obj: T) => U, g: (obj: T) => U, obj: T): U => {
  return f(obj) ?? g(obj);
});

export const preconcat = flip(concat);

export const propNonEq = curry((pred, key, obj) => {
  return !propEq(pred, key, obj);
});

export const safeFind = curry(<T>(pred: (x: T) => boolean, list: T[]): Maybe<T> => {
  const v = list.find(pred);
  return isNil(v) ? Maybe.Nothing() : Maybe.Just(v);
});

export const isTypeOf = curry(<K extends string>(property: K, value: Record<K, any>): boolean => {
  return value[property] !== undefined;
});

export const defer = () => {
  let resolve, reject;
  const promise = new Promise((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });
  return {
    promise,
    resolve,
    reject,
  };
};

export const perfDiff = (prevExp: Function, newExp: Function, threshold = 0) => {
  const prevStart = Date.now();
  prevExp();
  const prevEla = Date.now() - prevStart;
  const newStart = Date.now();
  newExp();
  const newEla = Date.now() - newStart;
  if (prevEla > threshold || newEla > threshold) {
    logging.log(`[prev=${prevEla}][new=${newEla}]`);
  }
};

export const wrapLoading = <T>(call: () => Promise<T>, setLoading: (value: boolean) => void): Promise<T> => {
  setLoading(true);
  return call().finally(() => setLoading(false));
};
